// client/src/components/ProductForm.js
import React, { useState, useEffect } from 'react';
import {
  Card, CardContent, Typography, TextField, Button, Stack, MenuItem, Box, Avatar, Chip,
  FormControlLabel, Switch, IconButton, Divider,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useCategories } from '../context/CategoryContext';
import { getImageUrl, API_BASE_URL } from '../utils/media';

const emptyForm = { name: '', description: '', price: '', compareAtPrice: '', imageUrl: '', quantity: 0, category: '', subcategory: '', galleryFiles: [] };
const emptyVariant = () => ({ key: Math.random().toString(36).slice(2), color: '', size: '', price: '', quantity: 0, image_url: '' });

const toFormData = (product) => ({
  ...product,
  compareAtPrice: product.compare_at_price ?? '',
  galleryFiles: [],
});

const ProductForm = ({ product, onSave, onCancel }) => {
  const { categoryNames, subcategoriesFor } = useCategories();
  const [formData, setFormData] = useState(product ? toFormData(product) : emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [hasVariants, setHasVariants] = useState(false);
  // Whether the product had options when the form opened - switching them
  // off means its single stock number has to be saved even if untouched.
  const [hadVariants, setHadVariants] = useState(false);
  const [variants, setVariants] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  useEffect(() => {
    setFormData(product ? toFormData(product) : emptyForm);
    setHasVariants(false);
    setHadVariants(false);
    setVariants([]);
    setExistingImages(product?.imageUrl ? [product.imageUrl] : []);

    // The product list endpoints don't include variants/gallery (too expensive per row),
    // so pull the full detail once when opening the edit form for an existing product.
    if (product?.id) {
      fetch(`${API_BASE_URL}/api/products/${product.id}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!data) return;
          const images = [data.product.imageUrl, ...(data.product.images || []).map((i) => i.image_url)].filter(Boolean);
          setExistingImages(images);
          if (data.product.variants && data.product.variants.length > 0) {
            setHasVariants(true);
            setHadVariants(true);
            setVariants(data.product.variants.map((v) => ({
              key: `v${v.id}`, ...v, price: v.price ?? '', originalQuantity: v.quantity,
            })));
          }
        })
        .catch(() => {});
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'images') {
      setFormData((prev) => ({ ...prev, galleryFiles: [...prev.galleryFiles, ...Array.from(e.target.files)] }));
    } else if (e.target.type === 'file') {
      setFormData((prev) => ({ ...prev, [name]: e.target.files[0] }));
    } else if (name === 'category') {
      // A subcategory from the old category won't necessarily make sense under the new one.
      setFormData((prev) => ({ ...prev, category: value, subcategory: '' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const subcategoryOptions = subcategoriesFor(formData.category);

  const wasPrice = Number(formData.compareAtPrice);
  const currentPrice = Number(formData.price);
  const markdownPercent = wasPrice > currentPrice && currentPrice > 0
    ? Math.round((1 - currentPrice / wasPrice) * 100)
    : 0;

  const removeGalleryFile = (index) => {
    setFormData((prev) => ({ ...prev, galleryFiles: prev.galleryFiles.filter((_, i) => i !== index) }));
  };

  const updateVariant = (key, field, value) => {
    setVariants((prev) => prev.map((v) => (v.key === key ? { ...v, [field]: value } : v)));
  };
  const addVariantRow = () => setVariants((prev) => [...prev, emptyVariant()]);
  const removeVariantRow = (key) => setVariants((prev) => prev.filter((v) => v.key !== key));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const data = new FormData();
    for (const key in formData) {
      if (key === 'galleryFiles') {
        formData.galleryFiles.forEach((file) => data.append('images', file));
      } else if (key === 'image' && formData[key] instanceof File) {
        data.append('image', formData[key]);
      } else if (key !== 'image' && key !== 'imageUrl' && key !== 'images' && key !== 'quantity') {
        data.append(key, formData[key]);
      }
    }
    // Stock numbers are only sent when new or actually changed: the server
    // leaves an omitted one as it is, so sales made while this form was open
    // aren't undone by saving an unrelated edit.
    const baseQuantityChanged = !product
      || String(formData.quantity) !== String(product.quantity ?? '')
      || hadVariants;
    if (!hasVariants && baseQuantityChanged) data.append('quantity', formData.quantity);
    if (hasVariants) {
      // `id` keeps an existing option the same option (shoppers' carts point at it).
      data.append('variants', JSON.stringify(variants
        .filter((v) => v.color || v.size)
        .map(({ key, originalQuantity, quantity, ...rest }) => (
          rest.id && String(quantity) === String(originalQuantity) ? rest : { ...rest, quantity }
        ))));
    } else if (product) {
      // Explicitly clear out any variants this product used to have.
      data.append('variants', JSON.stringify([]));
    }
    try {
      await onSave(data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {product ? 'Edit Product' : 'Add a New Product'}
        </Typography>
        <Stack component="form" onSubmit={handleSubmit} spacing={2} sx={{ maxWidth: 640 }}>
          <TextField label="Product Name" name="name" value={formData.name} onChange={handleChange} placeholder="e.g., Classic T-Shirt" required fullWidth />
          <TextField label="Description" name="description" value={formData.description} onChange={handleChange} placeholder="Describe your product" required fullWidth multiline minRows={3} />
          <Stack direction="row" spacing={2}>
            <TextField select label="Category" name="category" value={formData.category || ''} onChange={handleChange} fullWidth>
              {categoryNames.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField
              select label="Subcategory" name="subcategory" value={formData.subcategory || ''} onChange={handleChange}
              fullWidth disabled={subcategoryOptions.length === 0}
              helperText={subcategoryOptions.length === 0 ? 'No subcategories for this category' : ' '}
            >
              <MenuItem value="">(None)</MenuItem>
              {subcategoryOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Price (R)" name="price" type="number" inputProps={{ step: 0.01 }} value={formData.price} onChange={handleChange} required fullWidth />
            <TextField
              label="Was price (R) - mark down" name="compareAtPrice" type="number" inputProps={{ step: 0.01 }}
              value={formData.compareAtPrice} onChange={handleChange} fullWidth
              helperText={
                markdownPercent
                  ? `On sale: ${markdownPercent}% off. Appears under Specials.`
                  : 'Enter the old price to mark this product down. Leave blank for no sale.'
              }
            />
          </Stack>
          <TextField
            label="Stock Quantity" name="quantity" type="number" value={formData.quantity} onChange={handleChange}
            inputProps={{ min: 0, step: 1 }}
            required={!hasVariants} disabled={hasVariants} sx={{ maxWidth: 220 }}
            helperText={hasVariants ? 'Managed per-variant below' : ' '}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {formData.imageUrl && typeof formData.imageUrl === 'string' && (
              <Avatar src={getImageUrl(formData.imageUrl)} variant="rounded" sx={{ width: 56, height: 56 }} />
            )}
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
              Upload Cover Image
              <input hidden name="image" type="file" accept="image/*" onChange={handleChange} />
            </Button>
          </Box>

          <Box>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} size="small">
              Add Gallery Photos
              <input hidden name="images" type="file" accept="image/*" multiple onChange={handleChange} />
            </Button>
            {formData.galleryFiles.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                {formData.galleryFiles.map((file, index) => (
                  <Chip key={index} label={file.name} onDelete={() => removeGalleryFile(index)} size="small" />
                ))}
              </Stack>
            )}
          </Box>

          <Divider />

          <FormControlLabel
            control={<Switch checked={hasVariants} onChange={(e) => setHasVariants(e.target.checked)} />}
            label="This product has options (e.g. color / size)"
          />

          {hasVariants && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Each row is one purchasable combination with its own stock. Leave Price blank to use the base price above.
                Variant images can only be chosen from photos already uploaded — save once, then edit again to assign them.
              </Typography>
              <Stack spacing={2}>
                {variants.map((v) => (
                  <Stack key={v.key} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <TextField label="Color" size="small" value={v.color || ''} onChange={(e) => updateVariant(v.key, 'color', e.target.value)} sx={{ width: { sm: 130 } }} />
                    <TextField label="Size" size="small" value={v.size || ''} onChange={(e) => updateVariant(v.key, 'size', e.target.value)} sx={{ width: { sm: 100 } }} />
                    <TextField label="Price (R, optional)" size="small" type="number" inputProps={{ step: 0.01 }} value={v.price ?? ''} onChange={(e) => updateVariant(v.key, 'price', e.target.value)} sx={{ width: { sm: 140 } }} />
                    <TextField label="Stock" size="small" type="number" inputProps={{ min: 0, step: 1 }} value={v.quantity ?? 0} onChange={(e) => updateVariant(v.key, 'quantity', e.target.value)} sx={{ width: { sm: 90 } }} />
                    <TextField
                      select label="Image" size="small" value={v.image_url || ''} onChange={(e) => updateVariant(v.key, 'image_url', e.target.value)} sx={{ width: { sm: 140 } }}
                    >
                      <MenuItem value="">(default)</MenuItem>
                      {existingImages.map((url) => (
                        <MenuItem key={url} value={url}>
                          <Avatar src={getImageUrl(url)} variant="rounded" sx={{ width: 20, height: 20, mr: 1, display: 'inline-flex' }} />
                          Photo
                        </MenuItem>
                      ))}
                    </TextField>
                    <IconButton color="error" onClick={() => removeVariantRow(v.key)} aria-label="Remove variant">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button startIcon={<AddIcon />} onClick={addVariantRow} size="small" sx={{ alignSelf: 'flex-start' }}>
                  Add Option
                </Button>
              </Stack>
            </Box>
          )}

          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Product'}
            </Button>
            <Button type="button" variant="text" onClick={onCancel}>Cancel</Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProductForm;
